import { TemplateElement, html, defineElement, when, unsafeHTML } from '@webtides/element-js';
import style from './image-text.css';
import '../../../button/button.js';
import '../../../kicker/kicker.js';
import '../rive-animation/rive-animation.js';
import { responsiveImage } from '../../../../directives/images.js';

/**
 * @property {'left' | 'right'} variant
 * @property {KickerProperties} kicker - Chip label with text and variant
 * @property {string} headline
 * @property {string} text
 * @property {LinkProperties[]} links
 * @property {ImageProperties} image
 * @property {Object} riveAnimation - Optional Rive animation overlay config
 */
export default class ImageText extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            variant: 'right',
            kicker: null,
            headline: '',
            text: '',
            links: [],
            image: undefined,
            riveAnimation: null,
        };
    }

    template() {
        const ariaLabel = this.kicker?.text ? `${this.kicker.text} - ${this.headline}` : this.headline;
        return html`
            <div class="@container container container-lg">
                <div class="flex flex-col @md:flex-row items-center gap-fix-m @md:gap-fix-l">
                    <div class="@md:w-1/2 ${this.variant === 'right' ? '@md:order-last' : ''}">
                        <div class="relative">
                            ${when(
                                this.image?.srcset,
                                responsiveImage(
                                    this.image,
                                    'w-full rounded-sm',
                                    false,
                                    'auto',
                                    '(min-width: 1440px) 560px, (min-width: 1024px) 38vw, (min-width: 768px) 45vw, 100vw',
                                ),
                            )}
                            ${when(
                                this.riveAnimation?.src,
                                html`
                                    <mb-rive-animation
                                        class="absolute inset-0"
                                        src="${this.riveAnimation?.src}"
                                        artboard="${this.riveAnimation?.artboard || ''}"
                                        state-machine="${this.riveAnimation?.stateMachine || ''}"
                                        fit="${this.riveAnimation?.fit || 'contain'}"
                                        alignment="${this.riveAnimation?.alignment || 'center'}"
                                        trigger="${this.riveAnimation?.trigger || 'onView'}"
                                        pause-out-of-view="${this.riveAnimation?.pauseOutOfView ?? true}"
                                    ></mb-rive-animation>
                                `,
                            )}
                        </div>
                    </div>
                    <div class="@md:w-1/2">
                        <div class="flex flex-col gap-fix-s @md:gap-fix-m">
                            ${when(
                                this.kicker?.text,
                                html`
                                    <mb-kicker
                                        text="${this.kicker.text}"
                                        variant="${this.kicker.variant || ''}"
                                    ></mb-kicker>
                                `,
                            )}
                            <h3
                                class="mb-0 text-balance fix-title3-medium @md:fix-title2-medium"
                                aria-label="${ariaLabel}"
                            >
                                ${this.headline}
                            </h3>
                            ${when(this.text, html` <div class="richtext mb-0">${unsafeHTML(this.text)}</div> `)}
                            ${when(
                                this.links.length > 0,
                                html`
                                    <div class="flex flex-col @md:flex-row items-center gap-8">
                                        ${this.links.map(
                                            (link, index) => html`
                                                <mb-button
                                                    class="w-full @md:w-auto"
                                                    href="${link?.url}"
                                                    variant="${index === 0 ? 'secondary' : 'quaternary'}"
                                                    element="image text"
                                                >
                                                    ${link?.text}
                                                </mb-button>
                                            `,
                                        )}
                                    </div>
                                `,
                            )}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

defineElement('mb-image-text', ImageText);
